import { borderRadius, palette, spaces } from '@kimuno/theme';
import {
	FormHelperText,
	InputBase,
	type SxProps,
	type Theme,
	Typography,
	type TypographyProps,
} from '@mui/material';
import type React from 'react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Mirrors the API's MAX_TITLE_LENGTH.
const MAX_TITLE_LENGTH = 256;

// The input inherits the heading's font, so at rest the title looks like plain
// text; the padding is cancelled by the margin so the text doesn't move.
const inputRootStyles = {
	display: 'flex',
	font: 'inherit',
	letterSpacing: 'inherit',
	color: 'inherit',
	padding: `0 ${spaces.xs}`,
	margin: `0 calc(-1 * ${spaces.xs})`,
	borderRadius: borderRadius.xs,
	// Touch devices keep :hover after a tap; there the field goes straight to its active look.
	'@media (hover: hover)': {
		'&:hover': { backgroundColor: palette.action.hover },
	},
	'&.Mui-focused': {
		boxShadow: `inset 0 0 0 1px ${palette.primary.main}`,
	},
	'&.Mui-error': {
		boxShadow: `inset 0 0 0 1px ${palette.error.main}`,
	},
	'& .MuiInputBase-input': {
		height: 'auto',
		padding: 0,
		textOverflow: 'ellipsis',
	},
};

type Refusal = { text: string; message: string };

type Props = {
	/** The last saved title. */
	value: string;
	/** Called once per committed change with the trimmed title; a returned promise keeps the committed text shown until it settles. */
	onSave: (title: string) => Promise<unknown> | void;
	variant: NonNullable<TypographyProps['variant']>;
	/** The element the variant renders as; a `p` variant needs `div` since the field is a `div` itself. */
	component?: keyof React.JSX.IntrinsicElements;
	sx?: SxProps<Theme>;
	inputTestId?: string;
	/** The field's accessible name; "Title" by default. */
	label?: string;
	maxLength?: number;
	/**
	 * What a rejected save leaves in the field: the saved `value` again (`revert`), or the refused
	 * text with the rejection's `message` beneath it (`keep`) until the next change, Escape, or a
	 * retry on blur or Enter.
	 */
	onSaveFailure?: 'revert' | 'keep';
};

const InlineEditableTitle: React.FC<Props> = ({
	value,
	onSave,
	variant,
	component,
	sx,
	inputTestId,
	label,
	maxLength = MAX_TITLE_LENGTH,
	onSaveFailure = 'revert',
}) => {
	const { t } = useTranslation();
	const helperTextId = useId();
	// Null while the field isn't focused, so a new `value` shows at rest but never overwrites typing.
	const [draft, setDraft] = useState<string | null>(null);
	const [committed, setCommitted] = useState<string | null>(null);
	const [refusal, setRefusal] = useState<Refusal | null>(null);
	const latestSave = useRef<Promise<unknown> | null>(null);

	const savedTitle = committed ?? value;
	// A refused text outlives a new `value`: a poll must not replace what the teacher still sees.
	const restingTitle = refusal?.text ?? savedTitle;

	const save = (title: string) => {
		const result = onSave(title);
		if (!(result instanceof Promise)) {
			return;
		}
		setCommitted(title);
		setRefusal(null);
		latestSave.current = result;
		const settle = (error?: Error) => {
			if (latestSave.current !== result) {
				return;
			}
			setCommitted(null);
			if (error && onSaveFailure === 'keep') {
				setRefusal({ text: title, message: error.message });
			}
		};
		result.then(
			() => settle(),
			(error: Error) => settle(error)
		);
	};

	const handleBlur = () => {
		const title = (draft ?? '').trim();
		setDraft(null);
		if (title && title !== savedTitle) {
			save(title);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.currentTarget.blur();
		} else if (event.key === 'Escape' && draft !== savedTitle) {
			// Escape cancels only the edit and keeps focus inside a surrounding modal dialog;
			// with nothing left to cancel it propagates, so the next Escape closes the dialog.
			event.stopPropagation();
			setDraft(savedTitle);
			setRefusal(null);
		}
	};

	// The heading is named explicitly: browsers differ on whether its name reads an
	// embedded field's value or its label.
	return (
		<>
			<Typography
				variant={variant}
				variantMapping={component ? { [variant]: component } : undefined}
				sx={sx}
				aria-label={savedTitle}
			>
				<InputBase
					value={draft ?? restingTitle}
					error={refusal !== null}
					onFocus={() => setDraft(restingTitle)}
					onChange={(event) => {
						setDraft(event.target.value);
						setRefusal(null);
					}}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					inputProps={{
						'aria-label': label ?? t('Title'),
						'aria-describedby': refusal ? helperTextId : undefined,
						maxLength,
						'data-testid': inputTestId,
					}}
					sx={inputRootStyles}
				/>
			</Typography>
			{refusal && (
				<FormHelperText id={helperTextId} error role="alert">
					{refusal.message}
				</FormHelperText>
			)}
		</>
	);
};

export default InlineEditableTitle;
